/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {BaseComponent, RoutingModule} from "@intuitionrobotics/thunderstorm/frontend";
import {COLORS} from "@res/colors";
import {DB_Patient} from "@app/app-shared/patient";

import {FONTS} from "@res/fonts";
import * as emotion from "emotion";
import {TS_Table} from "../../../_TS_TABLE";
import {TableHeader} from "../../../utils/TableHeader";
import {TableContent} from "../../../utils/TableRow";
import {DB_User, getUserNameWithTitle} from "@app/ir-q-app-common/types/care-center/user";
import {tableCardStyle, TableRow} from "../../home/PatientsHome";
import {Loader} from "../../../../widgets/Loader";
import {calculatePagination, PaginationComponent} from "../../widget/PaginationComponent";
import {navigateToPatientPage, PatientsDataModule} from "@modules/PatientDataModule";
import {PatientsContext, PatientsMap} from "./PatientsContext";
import {PARAM_CONTACT_ID, Route_Messages, Route_PatientsHome} from "../../../Routes";
import {getUserOrgRoles} from "@modules/utils";
import {ID_ALL, messagingRoles, OrgRoleLevel} from "@app/app-shared/permission";


type Sort = { filterColumn: keyof TableRow | "newFirst", invert: boolean };
type Props = {
    content: TableRow[],
}


type State = {
    page: number,
    sort: Sort
}

export const rowHeight = 47;
export const headHeight = 66;

export class PatientsTable
    extends BaseComponent<Props, State> {

    private tableRef = React.createRef<HTMLDivElement>();

    private header: (keyof TableRow)[] = ["patientName",
        "unitId",
        "lastInteraction",
        "physician",
        "careManager"];

    constructor(props: Props) {
        super(props);
        this.state = {
            page: 1,
            sort: {filterColumn: "newFirst", invert: true}
        }
    }

    componentDidMount() {
        this.queryLastActivity();
    }

    private queryLastActivity() {
        const patients = this.props.content;
        const patientsUnitId = patients.map(patient => patient.unitId.unitId)
        PatientsDataModule.fetchLastUpdatedByPatients(patientsUnitId);
    }

    private sort(content: TableRow[]) {
        switch (this.state.sort.filterColumn) {
            case "patientName":
                return this.sortByPatient(content);
            case "unitId":
                return this.sortByUnitId(content);
            case "careManager":
                return this.sortByCareManager(content);
            case "physician":
                return this.sortByPhysician(content);
            case "lastInteraction":
                return this.sortByLastInteraction(content)
            case "newFirst":
                return this.sortNewFirst(content);
        }
        return content;
    }

    sortByPatient = (content: TableRow[]) => {
        return content.sort((a, b) => {
            const aName = a.patientName.name.toLowerCase();
            const bName = b.patientName.name.toLowerCase();
            return this.sortLogic(aName, bName, this.state.sort.invert);
        });
    };

    sortNewFirst = (content: TableRow[]) => {
        return content.sort((a, b) => {
            if (a.patientName._carePlanReviewed && !b.patientName._carePlanReviewed)
                return 1;
            if (!a.patientName._carePlanReviewed && b.patientName._carePlanReviewed)
                return -1;
            const aName = a.patientName.name.toLowerCase();
            const bName = b.patientName.name.toLowerCase();
            return this.sortLogic(aName, bName, this.state.sort.invert);
        });
    };

    sortByUnitId = (content: TableRow[]) => {
        return content.sort((a, b) => {
            const aUnitId = a.unitId.unitId;
            const bUnitId = b.unitId.unitId;
            return this.sortLogic(aUnitId, bUnitId, this.state.sort.invert);
        })
    }

    sortByLastInteraction = (content: TableRow[]) => {
        return content.sort((a, b) => {
            const aLastInteractionTimestamp = a.lastInteraction.timestamp;
            const bLastInteractionTimestamp = b.lastInteraction.timestamp;
            return this.sortLogic(aLastInteractionTimestamp, bLastInteractionTimestamp, this.state.sort.invert);
        })
    }

    sortByCareManager = (content: TableRow[]) => {
        return content.sort((a, b) => {
            const aCareManager = a.careManager.careManager?.last;
            const bCareManager = b.careManager.careManager?.last;
            return this.sortLogic(aCareManager, bCareManager, this.state.sort.invert);
        });
    };

    sortByPhysician = (content: TableRow[]) => {
        return content.sort((a, b) => {
            const aPhysician = a.physician.physician?.last;
            const bPhysician = b.physician.physician?.last;
            return this.sortLogic(aPhysician, bPhysician, this.state.sort.invert);
        });
    };

    rerouteToPatientDashboard = (patients: PatientsMap, id?: string) => {
        if (!id)
            return;
        const patient = patients[id];
        if (!patient)
            return;

        const userOrgRoles = getUserOrgRoles();
        const userOrgRole = userOrgRoles[ID_ALL] || userOrgRoles[patient.organizationId] || [];
        if (userOrgRole.find(role => role === OrgRoleLevel.USER_DATA))
            navigateToPatientPage(patient, Route_PatientsHome);
        else if (userOrgRole.find(role => messagingRoles.includes(role)))
            RoutingModule.goToRoute(Route_Messages, {[PARAM_CONTACT_ID]: patient.contactId});
    };

    private renderBody = (content: TableRow[], pagination: { startIndex: number; endIndex: number }) => {
        const tableContent = content.slice(pagination.startIndex, pagination.endIndex);

        return <PatientsContext.Consumer>{
            ({patients}) => {
                return <div ref={this.tableRef} style={{...tableCardStyle, flex: 999, overflow: "hidden"}}>
                    <TS_Table<TableRow>
                        id={"all-patients"}
                        header={this.header}
                        rows={tableContent}
                        td={{
                            style: {borderBottom: `1px solid #f1f5f8`}
                        }}
                        tr={() => {
                            return {
                                style: {
                                    borderBottom: `1px solid #f1f5f8`,
                                    cursor: "pointer"
                                },
                                className: emotion.css(
                                    {
                                        backgroundColor: "transparent",
                                        ":hover": {backgroundColor: "#f1f5f8"}
                                    }
                                )
                            };
                        }}
                        th={() => ({
                            className: emotion.css({
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                                height: 34,
                                backgroundColor: COLORS.color_gray_medium,
                                minWidth: "100px"
                            })
                        })}
                        style={{width: "100%"}}
                        headerRenderer={{
                            patientName: () => <TableHeader
                                title={"Customer"}
                                color={COLORS.color_gray_medium}
                                options={{sortable: true}}
                                column={"patientName"}
                                sort={this.sortObj("patientName")}
                            />,
                            unitId: () => <TableHeader
                                title={"Unit ID"}
                                color={COLORS.color_gray_medium}
                                options={{sortable: true}}
                                column={"unitId"}
                                sort={this.sortObj("unitId")}
                            />,
                            lastInteraction: () => <TableHeader
                                title={"Last Interaction"}
                                color={COLORS.color_gray_medium}
                                options={{sortable: true}}
                                column={"lastInteraction"}
                                sort={this.sortObj("lastInteraction")}
                            />,
                            physician: () => <TableHeader
                                title={"Physician"}
                                color={COLORS.color_gray_medium}
                                options={{sortable: true}}
                                column={"physician"}
                                sort={this.sortObj("physician")}
                            />,
                            careManager: () => <TableHeader
                                title={"Care Manager"}
                                color={COLORS.color_gray_medium}
                                options={{sortable: true}}
                                column={"careManager"}
                                sort={this.sortObj("careManager")}
                            />
                        }}
                        cellRenderer={{
                            patientName: (cellValue: DB_Patient) => (
                                <div
                                    style={{alignSelf: "flex-start", height: "35px"}}
                                    className="ll_h_c"
                                    onClick={() => this.rerouteToPatientDashboard(patients, cellValue._id)}
                                >
                                    <TableContent content={cellValue.name} id={cellValue._id}/>
                                    {
                                        !cellValue._carePlanReviewed &&
                                        FONTS.bold_16("New", {color: COLORS.purple_red, marginLeft: -10})
                                    }
                                </div>),
                            unitId: (cellValue: { unitId: string, id: string }) => <div style={{alignSelf: "flex-start", height: "35px"}} className="ll_h_c"
                                                                                        onClick={() => this.rerouteToPatientDashboard(patients, cellValue.id)}>
                                <TableContent content={cellValue.unitId} id={cellValue.id}/>
                            </div>,
                            lastInteraction: (cellValue: { lastInteraction: string, timestamp: number, id: string }) => <div style={{alignSelf: "flex-start", height: "35px"}} className="ll_h_c"
                                                                                                                             onClick={() => this.rerouteToPatientDashboard(patients, cellValue.id)}>
                                <TableContent content={cellValue.lastInteraction} id={cellValue.id}/>
                            </div>,
                            physician: (cellValue: { physician?: DB_User, id: string }) => <div
                                onClick={() => this.rerouteToPatientDashboard(patients, cellValue.id)}>
                                {
                                    <TableContent content={cellValue.physician ? getUserNameWithTitle(cellValue.physician) : ""}
                                                  id={cellValue.id}/>
                                }
                            </div>,
                            careManager: (cellValue: { careManager?: DB_User, id: string }) => <div
                                onClick={() => this.rerouteToPatientDashboard(patients, cellValue.id)}>
                                {
                                    <TableContent content={cellValue.careManager ? getUserNameWithTitle(cellValue.careManager) : ""}
                                                  id={cellValue.id}/>
                                }
                            </div>
                        }}
                    />
                </div>;

            }
        }</PatientsContext.Consumer>
    };

    private renderPagination = (content: TableRow[], pagination: { startIndex: number; endIndex: number }) => (
        <PaginationComponent
            tableContent={content}
            setCurrentPage={(page) => this.setState({page})}
            pagination={pagination}
        />);

    private sortLogic = (aVariable?: string | number, bVariable?: string | number, invert?: boolean): -1 | 0 | 1 => {
        return (this.sortImpl(aVariable, bVariable) * (invert ? -1 : 1)) as -1 | 1 | 0;
    };

    private sortImpl = (aVariable?: string | number, bVariable?: string | number): -1 | 0 | 1 => {
        if (!aVariable && !!bVariable)
            return 1;
        if (!bVariable && !!aVariable)
            return -1;
        if (!aVariable && !bVariable)
            return 0;
        // @ts-ignore
        if (aVariable > bVariable)
            return -1;
        return 1;
    };


    sortObj = (name: "patientName" | "unitId" | "careManager" | "physician" | "lastInteraction") => ({
        func: () => this.setState({
            sort: {
                filterColumn: name,
                invert: (this.state.sort.filterColumn === "newFirst" && name === "patientName")
                    ? true
                    : (this.state.sort.filterColumn === name) ? !this.state.sort.invert : true
            }
        }),
        up: this.state.sort.filterColumn !== name ? undefined : this.state.sort.invert
    });

    render() {
        if (!this.props.content)
            return <div style={{flex: 10000}}><Loader/></div>

        const tableContent = this.sort(this.props.content)
        const pagination = calculatePagination(this.state.page, this.tableRef.current);
        return <>
            {this.renderBody(tableContent, pagination)}
            {this.renderPagination(tableContent, pagination)}
        </>;
    }
}
